import { DevToolsMicroLabel } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsMicroLabel';
import type { DebugCallQualityProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugRecord/DebugCallQuality/index.types';
import type {
  DevToolsTone,
} from 'Modules/softphone/components/SoftphoneDevTools/index.types';
import {
  CARD_TITLE,
  SURFACE,
  TONE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import React from 'react';
import { cn } from '~/lib/utils';

const worseWhenHigher = (
  value: number,
  warnAbove: number,
  badAbove: number
): DevToolsTone => {
  if (value < warnAbove) {
    return 'ok';
  }

  return value < badAbove ? 'warn' : 'bad';
};

const worseWhenLower = (
  value: number,
  warnBelow: number,
  badBelow: number
): DevToolsTone => {
  if (value > warnBelow) {
    return 'ok';
  }

  return value > badBelow ? 'warn' : 'bad';
};

/**
 * DebugCallQuality - What the audio was actually like
 *
 * Connection health above it says the path came up; this says whether it
 * carried anything worth listening to.
 */
export const DebugCallQuality: React.FC<DebugCallQualityProps> = ({ info }) => {
  const quality = info.quality;

  if (!quality) {
    // Silence here would read as "nothing to report". It means "not measured",
    // and the difference decides whether audio is ruled out as a cause.
    return (
      <div className={SURFACE.card}>
        <div className={cn(SURFACE.cardHeader, CARD_TITLE)}>Call quality</div>
        <p className="px-3 py-2.5 text-[11.5px] leading-[1.55] text-zinc-400">
          Not measured for this leg. Monitoring follows the current call and
          stops for conference participants once their peer connections close,
          so no figures here does not mean no problems.
        </p>
      </div>
    );
  }

  const readings: { label: string; value: string; tone: DevToolsTone }[] = [];

  if (quality.mosScore !== undefined) {
    readings.push({
      label: 'MOS',
      value: quality.mosScore.toFixed(2),
      tone: worseWhenLower(quality.mosScore, 3.6, 2.6),
    });
  }

  if (quality.averageJitter !== undefined) {
    const jitterMs = Math.round(quality.averageJitter * 1000);

    readings.push({
      label: 'Avg jitter',
      value: `${jitterMs}ms`,
      tone: worseWhenHigher(jitterMs, 30, 50),
    });
  }

  if (quality.totalPacketsLost !== undefined) {
    readings.push({
      label: 'Packets lost',
      value: String(quality.totalPacketsLost),
      tone: worseWhenHigher(quality.totalPacketsLost, 1, 50),
    });
  }

  if (quality.averageRoundTripTime !== undefined) {
    const rttMs = Math.round(quality.averageRoundTripTime * 1000);

    readings.push({
      label: 'Avg round trip',
      value: `${rttMs}ms`,
      tone: worseWhenHigher(rttMs, 150, 300),
    });
  }

  if (quality.availableBitrate !== undefined) {
    readings.push({
      label: 'Available bitrate',
      value: `${Math.round(quality.availableBitrate / 1000)}k`,
      tone: 'muted',
    });
  }

  return (
    <div className={SURFACE.card}>
      <div className={cn('flex items-center gap-2', SURFACE.cardHeader)}>
        <span className={CARD_TITLE}>Call quality</span>
        <div className="flex-1" />
        {quality.codec && (
          <span className="font-sp-mono text-[10px] text-zinc-400">
            {quality.codec.name}
            {quality.codec.clockRate ? `/${quality.codec.clockRate}` : ''}
            {quality.codec.channels ? `/${quality.codec.channels}` : ''}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-1.5 p-3">
        {readings.map(({ label, value, tone }) => (
          <div
            key={label}
            className={cn(
              'flex items-center gap-2 px-[9px] py-[7px]',
              TONE[tone].inset
            )}
          >
            <span
              className={cn(
                'h-1.5 w-1.5 flex-shrink-0 rounded-full',
                TONE[tone].dot
              )}
            />
            <span className="min-w-0 flex-1 truncate text-[11px] text-zinc-400">
              {label}
            </span>
            <span
              className={cn(
                'font-sp-mono text-[11px] font-medium',
                TONE[tone].text
              )}
            >
              {value}
            </span>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 border-t border-zinc-800 px-3 py-2">
        <DevToolsMicroLabel>Samples</DevToolsMicroLabel>
        <span className="font-sp-mono text-[11px] text-zinc-300">
          {quality.sampleCount}
        </span>
        <span className="text-[10.5px] text-zinc-400">
          collected every 2s while the call ran
        </span>
      </div>
    </div>
  );
};
